'use client'

import { IBlogPost } from '@tebuto/api/blog/tebutoClient'
import { ArrowRight, Clock } from 'lucide-react'
import { DateTime } from 'luxon'
import Image from 'next/image'
import Link from 'next/link'
import { AuthorLabel } from './AuthorLabel'

interface IBlogPostPreviewProps {
    post: IBlogPost
    featured?: boolean
}

export function BlogPostPreview({ post, featured }: IBlogPostPreviewProps) {
    const postUrl = `/blog/${post.slug}`
    const coverSrc = post.cover?.thumbnailURL ?? post.cover?.url ?? '/assets/preview.webp'

    if (featured) {
        return <FeaturedCard post={post} postUrl={postUrl} coverSrc={coverSrc} />
    }

    return (
        <Link
            href={postUrl}
            className="group flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-black/4 shadow-sm transition-all duration-300 hover:shadow-lg hover:ring-primary-200/60 hover:-translate-y-0.5"
        >
            {/* Image */}
            <div className="relative aspect-16/10 w-full overflow-hidden bg-primary-25">
                <Image
                    height={400}
                    width={600}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    alt={post.title}
                    src={coverSrc}
                />
                <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/20 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col justify-between p-6">
                <div>
                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-1.5">
                            {post.tags.map((tag) => (
                                <span
                                    key={tag.name}
                                    className="rounded-full bg-primary-50 px-2.5 py-0.5 text-xxs font-semibold text-primary-700"
                                >
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Title */}
                    <h2 className="text-lg font-bold leading-snug text-foreground transition-colors duration-200 group-hover:text-primary-700">
                        {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-foreground-tertiary">
                        {post.excerpt}
                    </p>
                </div>

                {/* Footer */}
                <div className="mt-5 flex items-center justify-between border-t border-primary-50 pt-4">
                    <div className="flex items-center gap-2.5">
                        <div className="size-7 shrink-0 overflow-hidden rounded-full ring-1 ring-primary-100">
                            <Image
                                alt={post.author?.name ?? 'Tebuto'}
                                height={28}
                                width={28}
                                className="h-full w-full object-cover"
                                src={post.author?.avatar?.url ?? '/assets/favicon/FaviconRect.png'}
                            />
                        </div>
                        <span className="text-xs font-medium text-foreground-secondary">
                            <AuthorLabel author={post.author} />
                        </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-foreground-muted">
                        <Clock className="size-3.5" />
                        <span>{post.readingTime} Min.</span>
                    </div>
                </div>
            </div>
        </Link>
    )
}

function FeaturedCard({ post, postUrl, coverSrc }: { post: IBlogPost; postUrl: string; coverSrc: string }) {
    return (
        <Link
            href={postUrl}
            className="group grid overflow-hidden rounded-2xl bg-white ring-1 ring-black/4 shadow-sm transition-all duration-300 hover:shadow-lg hover:ring-primary-200/60 lg:grid-cols-2"
        >
            {/* Image */}
            <div className="relative aspect-16/10 w-full overflow-hidden bg-primary-25 lg:aspect-auto lg:h-full">
                <Image
                    height={600}
                    width={800}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    alt={post.title}
                    src={coverSrc}
                />
                <div className="pointer-events-none absolute inset-0 bg-linear-to-r from-black/10 via-transparent to-transparent" />
            </div>

            {/* Content */}
            <div className="flex flex-col justify-center p-8 lg:p-10">
                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                            <span
                                key={tag.name}
                                className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700 ring-1 ring-primary-200/60"
                            >
                                {tag.name}
                            </span>
                        ))}
                    </div>
                )}

                {/* Title */}
                <h2 className="text-2xl font-bold leading-tight text-foreground transition-colors duration-200 group-hover:text-primary-700 lg:text-3xl">
                    {post.title}
                </h2>

                {/* Excerpt */}
                <p className="mt-3 line-clamp-3 text-base leading-relaxed text-foreground-tertiary lg:text-lg">
                    {post.excerpt}
                </p>

                {/* Meta */}
                <div className="mt-6 flex items-center gap-4">
                    <div className="flex items-center gap-2.5">
                        <div className="size-8 shrink-0 overflow-hidden rounded-full ring-2 ring-primary-100">
                            <Image
                                alt={post.author?.name ?? 'Tebuto'}
                                height={32}
                                width={32}
                                className="h-full w-full object-cover"
                                src={post.author?.avatar?.url ?? '/assets/favicon/FaviconRect.png'}
                            />
                        </div>
                        <div className="text-sm">
                            <span className="font-medium text-foreground-secondary">
                                <AuthorLabel author={post.author} />
                            </span>
                            <span className="mx-2 text-foreground-subtle">&bull;</span>
                            <span className="text-foreground-tertiary">
                                {DateTime.fromISO(post.createdAt).toFormat('dd.MM.yyyy')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Read more */}
                <div className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-all duration-200 group-hover:gap-2.5">
                    Artikel lesen
                    <ArrowRight className="size-4" />
                </div>
            </div>
        </Link>
    )
}
